import { el } from '../dom.js';
import {
  LIGHTING, DEFAULT_LIGHTING, WIND, SCENARIOS, SCENARIO_CATEGORIES,
  COMBAT_SPAWNS, DEFAULT_COMBAT_SPAWN,
} from '../../core/SimConfig.js';
import { getGrade, getBest } from '../../mission/MissionRecords.js';

const AIM9_MAX = 4;
const AGM65_MAX = 6;

// Cloud-coverage slider granularity, as a fraction of sky covered (1% steps).
const CLOUD_COVERAGE_STEP = 0.01;

// METAR-style sky-cover abbreviations, keyed by the coverage fraction they cap.
// Matched top-down, so the first entry whose `max` the value falls under wins.
const SKY_COVER = [
  { max: 0.001, label: 'SKC' },  // sky clear
  { max: 0.25,  label: 'FEW' },
  { max: 0.5,   label: 'SCT' },  // scattered
  { max: 0.9,   label: 'BKN' },  // broken
  { max: Infinity, label: 'OVC' },
];

/** The tuned cloud coverage a lighting condition ships with (0 when it has no deck). */
function conditionCoverage(key) {
  return LIGHTING[key]?.clouds?.coverage ?? 0;
}

// Detail-card header image shown when a scenario doesn't define its own `image`.
const MISSION_IMAGE_FALLBACK = '/assets/ui/mission_placeholder.jpg';

/**
 * Start screen — a left-docked configuration panel over the live 3D viewport.
 *
 * Collects the launch configuration (weather / wind / armament / scenario) and,
 * on "Fly Now", slides the panel away before emitting `onLaunch(config)`.
 * Weather changes preview live via `onLightingChange`; the secondary buttons
 * (Settings / Credits / Controls) open overlays via `onOpenOverlay`.
 */
export default class StartScreen {
  constructor({ onLaunch, onLightingChange, onArmamentChange, onCloudsChange, onOpenOverlay, onNoHudChange, onReviewResult }) {
    this.onLaunch = onLaunch;
    this.onLightingChange = onLightingChange;
    this.onArmamentChange = onArmamentChange;
    this.onCloudsChange = onCloudsChange;
    this.onOpenOverlay = onOpenOverlay;
    this.onNoHudChange = onNoHudChange;
    this.onReviewResult = onReviewResult;

    // Mirrors the cloud-quality graphics setting; seeded by UIManager from the
    // saved value once the overlays exist. See {@link setCloudsEnabled}.
    this._cloudsEnabled = true;

    this.selection = {
      lighting: DEFAULT_LIGHTING,
      wind: WIND.defaultPreset,
      cloudCoverage: conditionCoverage(DEFAULT_LIGHTING),
      gau8: 'full',
      unlimited: false,
      flares: 'full',
      unlimitedFlares: false,
      aim9: AIM9_MAX,
      agm65: AGM65_MAX,
      category: SCENARIO_CATEGORIES[0],
      scenarioIndex: null,
      combatSpawn: DEFAULT_COMBAT_SPAWN,
      noHud: false,
    };

    this.el = el('div', 'screen start-screen');
    this._panel = el('aside', 'start-panel');
    this.el.appendChild(this._panel);

    this._buildHeader();
    const scroll = el('div', 'panel-scroll');
    this._panel.appendChild(scroll);
    this._buildScenarios(scroll);
    this._buildArmament(scroll);
    this._buildConditions(scroll);
    this._buildFooter();

    // Default-select the first scenario in the first category.
    this._selectCategory(this.selection.category);
  }

  // ── Header ────────────────────────────────────────────────────────────────
  _buildHeader() {
    const header = el('div', 'panel-header');
    header.appendChild(el('div', 'panel-logo'));
    this._panel.appendChild(header);
  }

  // ── Conditions (weather + wind + clouds) ────────────────────────────────────
  _buildConditions(parent) {
    const section = this._section(parent, 'Conditions');

    // No-HUD toggle — locked in at launch so it can't be changed mid-game.
    // Reversed sense: checked means the in-game HUD is hidden (off by default,
    // so the HUD is enabled by default). It shares the settings overlay's `hud`
    // value, so UIManager seeds it from the saved settings on boot.
    const noHudRow = el('div', 'arm-checks');
    this._noHudBox = this._checkbox(noHudRow, 'NO HUD',
      () => this.selection.noHud,
      (v) => { this.selection.noHud = v; this.onNoHudChange?.(v); });
    section.appendChild(noHudRow);

    section.appendChild(el('div', 'field-label', 'Weather / Lighting'));
    const weatherRow = el('div', 'btn-group');
    section.appendChild(weatherRow);
    this._refreshWeather = this._segGroup(
      weatherRow,
      Object.entries(LIGHTING).map(([key, cfg]) => ({ value: key, label: cfg.name })),
      () => this.selection.lighting,
      (v) => this.onLightingChange?.(v), // App applies + routes back to setLighting()
    );

    const windHead = el('div', 'field-label field-label--row');
    windHead.appendChild(el('span', null, 'Wind'));
    this._windReadout = el('span', 'wind-readout');
    windHead.appendChild(this._windReadout);
    section.appendChild(windHead);

    const windRow = el('div', 'btn-group');
    section.appendChild(windRow);
    this._refreshWind = this._segGroup(
      windRow,
      Object.entries(WIND.presets).map(([key, p]) => ({ value: key, label: p.label })),
      () => this.selection.wind,
      (v) => { this.selection.wind = v; this._refreshWind(); this._updateWindReadout(); },
    );
    this._updateWindReadout();

    this._buildClouds(section);
  }

  _updateWindReadout() {
    const p = WIND.presets[this.selection.wind];
    this._windReadout.textContent = p ? `${p.dir}° / ${p.kts} KTS` : '';
  }

  /**
   * Cloud coverage — a live override of the weather condition's tuned deck
   * coverage, previewed through `onCloudsChange` and carried into the launch
   * config. Selecting a different weather resets it to that condition's own
   * value (see {@link setLighting}); conditions with no deck hide the control.
   */
  _buildClouds(section) {
    this._cloudField = el('div', 'cloud-field');

    const head = el('div', 'field-label field-label--row');
    head.appendChild(el('span', null, 'Clouds'));
    this._cloudReadout = el('span', 'wind-readout');
    head.appendChild(this._cloudReadout);
    this._cloudField.appendChild(head);

    this._cloudSlider = this._slider(this._cloudField, {
      min: 0,
      max: 1,
      step: CLOUD_COVERAGE_STEP,
      get: () => this.selection.cloudCoverage,
      set: (v) => {
        this.selection.cloudCoverage = v;
        this._updateCloudReadout();
        this.onCloudsChange?.(v);
      },
    });
    // Kept so _syncClouds can disable the input outright rather than only
    // dimming it — a dimmed-but-focusable slider is still draggable by keyboard.
    this._cloudInput = this._cloudField.querySelector('.slider-input');

    section.appendChild(this._cloudField);
    this._syncClouds();
  }

  _updateCloudReadout() {
    if (!this._cloudsEnabled) {
      this._cloudReadout.textContent = 'Disabled';
      return;
    }
    const v = this.selection.cloudCoverage;
    const cover = SKY_COVER.find((c) => v < c.max).label;
    this._cloudReadout.textContent = `${cover} / ${Math.round(v * 100)}%`;
  }

  /**
   * Re-point the cloud control at the active condition, hiding it if it has no
   * deck and greying it out if the cloud-quality setting is Off — a coverage
   * the player can't see would be a lie, so the readout says so instead.
   */
  _syncClouds() {
    this._cloudField.classList.toggle('is-hidden', !LIGHTING[this.selection.lighting]?.clouds);
    this._cloudField.classList.toggle('is-disabled', !this._cloudsEnabled);
    this._cloudInput.disabled = !this._cloudsEnabled;
    this._cloudSlider();
    this._updateCloudReadout();
  }

  /**
   * Follow the Cloud quality graphics setting: Off disables the deck outright,
   * so the coverage slider is dimmed and inert until it is turned back on. The
   * selected coverage is kept either way, so it returns with the clouds.
   */
  setCloudsEnabled(on) {
    this._cloudsEnabled = !!on;
    if (this._cloudField) this._syncClouds();
  }

  // ── Armament ────────────────────────────────────────────────────────────────
  _buildArmament(parent) {
    const section = this._section(parent, 'Armament');
    // Hidden for scenarios that fly a fixed loadout (training) — see _renderDetail.
    this._armSection = section;

    // GAU-8 ammo — arrow stepper cycling between None / Full to match the
    // AIM-9 / AGM-65 steppers below (label left, ‹ value › control right).
    this._refreshGau8 = this._optionStepper(section, 'GAU-8 Avenger 30mm',
      [{ value: 'none', label: 'None' }, { value: 'full', label: 'Full' }],
      () => this.selection.gau8,
      (v) => { this.selection.gau8 = v; this._emitArmament(); });

    // AIM-9M loadout — 0 / 2 / 4. Stepper jumps by 2 (single-rail pair → full
    // double-rail set), updating the model live via _emitArmament().
    this._aim9Val = this._stepper(section, 'AIM-9M Sidewinder', AIM9_MAX,
      () => this.selection.aim9, (n) => { this.selection.aim9 = n; this._emitArmament(); }, 2);
    // AGM-65 loadout — 0 / 2 / 4 / 6. Stepper jumps by 2: 2 mounts on the
    // single-rail empties (no rail mesh); 4 or 6 use the triple-rail pylons.
    this._agm65Val = this._stepper(section, 'AGM-65 Maverick', AGM65_MAX,
      () => this.selection.agm65, (n) => { this.selection.agm65 = n; this._emitArmament(); }, 2);

    // Unlimited toggles, side by side.
    const checks = el('div', 'arm-checks');
    this._unlimited = this._checkbox(checks, 'Unlimited 30mm',
      () => this.selection.unlimited,
      (v) => { this.selection.unlimited = v; this._emitArmament(); });
    this._unlimitedFlares = this._checkbox(checks, 'Unlimited flares',
      () => this.selection.unlimitedFlares,
      (v) => { this.selection.unlimitedFlares = v; this._emitArmament(); });
    section.appendChild(checks);
  }

  /** Build a styled checkbox + label. Returns the input element. */
  _checkbox(parent, label, get, set) {
    const wrap = el('label', 'arm-check');
    const box = el('input', 'check-box');
    box.type = 'checkbox';
    box.checked = get();
    box.addEventListener('change', () => set(box.checked));
    wrap.appendChild(box);
    wrap.appendChild(el('span', null, label));
    parent.appendChild(wrap);
    return box;
  }

  /**
   * The loadout the selected mission will actually fly with: the player's
   * armament selection, unless the scenario pins one (training missions fly
   * clean, and hide the Armament section entirely — see {@link _renderDetail}).
   */
  _effectiveArmament(sel = this.selection) {
    const sc = sel.scenarioIndex != null ? SCENARIOS[sel.scenarioIndex] : null;
    const chosen = {
      gau8: sel.gau8,
      unlimited: sel.unlimited,
      flares: sel.flares,
      unlimitedFlares: sel.unlimitedFlares,
      aim9: sel.aim9,
      agm65: sel.agm65,
    };
    return sc?.loadout ? { ...chosen, ...sc.loadout } : chosen;
  }

  _emitArmament() {
    this.onArmamentChange?.(this._effectiveArmament());
  }

  /** Build an `‹ x N ›` quantity stepper. Returns a refresh fn for the value label. */
  _stepper(parent, label, max, get, set, step = 1) {
    const row = el('div', 'arm-row');
    row.appendChild(el('span', 'arm-name', label));

    const stepper = el('div', 'stepper');
    const dec = el('button', 'step-btn', '‹');
    const val = el('span', 'step-val');
    const inc = el('button', 'step-btn', '›');
    dec.type = inc.type = 'button';

    const refresh = () => { val.textContent = `× ${get()}`; };
    dec.addEventListener('click', () => { set(Math.max(0, get() - step)); refresh(); });
    inc.addEventListener('click', () => { set(Math.min(max, get() + step)); refresh(); });

    stepper.append(dec, val, inc);
    row.appendChild(stepper);
    parent.appendChild(row);
    refresh();
    return refresh;
  }

  /** Build a `‹ Label ›` stepper that cycles through discrete options. Returns a refresh fn. */
  _optionStepper(parent, label, options, get, set) {
    const row = el('div', 'arm-row');
    row.appendChild(el('span', 'arm-name', label));

    const stepper = el('div', 'stepper');
    const dec = el('button', 'step-btn', '‹');
    const val = el('span', 'step-val');
    const inc = el('button', 'step-btn', '›');
    dec.type = inc.type = 'button';

    const indexOf = () => Math.max(0, options.findIndex((o) => o.value === get()));
    const refresh = () => { val.textContent = options[indexOf()].label; };
    const cycle = (dir) => {
      const next = (indexOf() + dir + options.length) % options.length;
      set(options[next].value);
      refresh();
    };
    dec.addEventListener('click', () => cycle(-1));
    inc.addEventListener('click', () => cycle(1));

    stepper.append(dec, val, inc);
    row.appendChild(stepper);
    parent.appendChild(row);
    refresh();
    return refresh;
  }

  // ── Scenarios (category → mission browser) ──────────────────────────────────
  _buildScenarios(parent) {
    const section = this._section(parent, 'Scenarios');

    const cols = el('div', 'scenario-cols');

    const left = el('div', 'scenario-col');
    left.appendChild(el('div', 'field-label', 'Category'));
    this._catList = el('div', 'list');
    left.appendChild(this._catList);

    // Combat-only spawn picker. Hidden for other categories (which embed their
    // own fixed spawn); shown for Combat so the player can choose where to start.
    this._startField = el('div', 'start-field');
    this._startField.appendChild(el('div', 'field-label', 'Start'));
    this._startSelect = el('select', 'start-select');
    for (const s of COMBAT_SPAWNS) {
      const opt = el('option', null, s.label);
      opt.value = s.id;
      this._startSelect.appendChild(opt);
    }
    this._startSelect.value = this.selection.combatSpawn;
    this._startSelect.addEventListener('change', () => {
      this.selection.combatSpawn = this._startSelect.value;
    });
    this._startField.appendChild(this._startSelect);
    left.appendChild(this._startField);

    const right = el('div', 'scenario-col');
    right.appendChild(el('div', 'field-label', 'Mission'));
    this._missionList = el('div', 'list mission-list');
    right.appendChild(this._missionList);

    cols.append(left, right);
    section.appendChild(cols);

    // Category buttons (static — derived from SCENARIO_CATEGORIES).
    this._refreshCategory = this._listGroup(
      this._catList,
      SCENARIO_CATEGORIES.map((c) => ({ value: c, label: c })),
      () => this.selection.category,
      (c) => this._selectCategory(c),
    );

    // Detail box, with an image header above the text metadata.
    this._detail = el('div', 'scenario-detail');
    this._detailImage = el('div', 'detail-image');
    // Result overlay on the image (best saved run): a large grade badge image in
    // the top-left corner, and a grade-letter box + score in the top-right.
    // Clicking either the badge or the grade/score reopens the mission-ended
    // score sheet for the best saved run (see _reviewCurrent).
    this._detailBadge = el('button', 'detail-badge');
    this._detailBadge.type = 'button';
    this._detailBadge.title = 'View mission results';
    this._detailBadgeImg = el('img', 'detail-badge-img');
    this._detailBadgeImg.alt = '';
    this._detailBadge.appendChild(this._detailBadgeImg);
    this._detailBadge.addEventListener('click', () => this._reviewCurrent());
    this._detailResult = el('button', 'detail-result');
    this._detailResult.type = 'button';
    this._detailResult.title = 'View mission results';
    this._detailGrade = el('span', 'mission-grade');
    this._detailScore = el('span', 'detail-score');
    this._detailResult.append(this._detailGrade, this._detailScore);
    this._detailResult.addEventListener('click', () => this._reviewCurrent());
    this._detailImage.append(this._detailBadge, this._detailResult);
    this._detailCode = el('span', 'detail-code');
    this._detailEst = el('span', 'detail-est');
    const detailHead = el('div', 'detail-head');
    detailHead.append(this._detailCode, this._detailEst);
    this._detailTitle = el('div', 'detail-title');
    this._detailDesc = el('div', 'detail-desc');
    this._detail.append(this._detailImage, detailHead, this._detailTitle, this._detailDesc);
    section.appendChild(this._detail);
  }

  _selectCategory(category) {
    this.selection.category = category;
    this._refreshCategory();
    this._startField.classList.toggle('is-hidden', category !== 'Combat');

    const missions = SCENARIOS
      .map((sc, index) => ({ sc, index }))
      .filter(({ sc }) => (sc.category || 'Free Flight') === category);

    this.selection.scenarioIndex = missions.length ? missions[0].index : null;

    this._missionList.replaceChildren();
    this._missionButtons = [];
    if (missions.length === 0) {
      this._missionList.appendChild(el('div', 'list-empty', 'No missions available'));
    } else {
      // Grades are only recorded for combat missions, so only combat rows carry
      // a grade badge (training / free-flight rows show none).
      const graded = category === 'Combat';
      this._refreshMission = this._missionListGroup(
        this._missionList,
        missions.map(({ sc, index }) => ({ value: index, label: sc.mission || sc.name, code: graded ? sc.code : null })),
        () => this.selection.scenarioIndex,
        (index) => { this.selection.scenarioIndex = index; this._refreshMission(); this._renderDetail(); },
      );
    }
    this._renderDetail();
  }

  _renderDetail() {
    const idx = this.selection.scenarioIndex;
    const sc = idx != null ? SCENARIOS[idx] : null;
    this._detail.classList.toggle('is-empty', !sc);
    this._detailImage.style.backgroundImage = `url("${sc?.image || MISSION_IMAGE_FALLBACK}")`;
    this._renderDetailResult(sc);
    this._detailCode.textContent = sc ? sc.code || '' : '';
    this._detailEst.textContent = sc ? (sc.est ? `EST ${sc.est}` : '') : '';
    this._detailTitle.textContent = sc ? sc.name : 'Select a mission';
    this._detailDesc.textContent = sc ? sc.desc || '' : 'This category has no missions yet.';
    if (this._flyBtn) this._flyBtn.disabled = !sc;
    // A scenario with a pinned loadout takes the pylons out of the player's
    // hands, so hide the section and push its fixed load at the live preview.
    this._armSection?.classList.toggle('is-hidden', !!sc?.loadout);
    this._emitArmament();
  }

  /**
   * Overlay the best saved result on the detail image: a grade badge image in
   * the top-left and a grade-letter box + score in the top-right. Hidden when
   * the mission has no saved record (never played / non-combat).
   */
  _renderDetailResult(sc) {
    const best = sc ? getBest(sc.code) : null;
    this._detailImage.classList.toggle('has-result', !!best);
    if (!best) return;

    const grade = best.grade;
    this._detailBadgeImg.src = `/assets/ui/grade_${grade.toLowerCase()}.png`;
    this._detailGrade.textContent = grade;
    this._detailGrade.className = `mission-grade grade-${grade.toLowerCase()}`;
    this._detailScore.textContent = Math.round(best.score).toLocaleString('en-US');
  }

  /** Reopen the mission-ended score sheet for the selected mission's best saved run. */
  _reviewCurrent() {
    const idx = this.selection.scenarioIndex;
    const sc = idx != null ? SCENARIOS[idx] : null;
    if (sc?.code && getBest(sc.code)) this.onReviewResult?.(sc.code);
  }

  // ── Footer (Fly Now + secondary buttons + status) ───────────────────────────
  _buildFooter() {
    const footer = el('div', 'panel-footer');

    this._flyBtn = el('button', 'fly-btn');
    this._flyBtn.type = 'button';
    this._flyBtn.innerHTML = '<span>&gt;&gt;</span> FLY NOW <span>&lt;&lt;</span>';
    this._flyBtn.addEventListener('click', () => this._launch());
    footer.appendChild(this._flyBtn);

    const secondary = el('div', 'footer-btns');
    for (const [key, label] of [['settings', 'Settings'], ['credits', 'Credits'], ['controls', 'Controls']]) {
      const btn = el('button', 'footer-btn', label);
      btn.type = 'button';
      btn.addEventListener('click', () => this.onOpenOverlay?.(key));
      secondary.appendChild(btn);
    }
    footer.appendChild(secondary);

    const status = el('div', 'panel-status');
    status.appendChild(el('span', 'status-dot'));
    status.appendChild(el('span', null, 'MISSION COMPUTER READY'));
    status.appendChild(el('span', 'status-loc', 'BMGR Yuma, AZ'));
    footer.appendChild(status);

    this._panel.appendChild(footer);
  }

  _launch() {
    if (this._launching || this._flyBtn.disabled) return;
    this._launching = true;
    const config = this.launchConfig();

    // Slide the panel away, then launch when the transition finishes.
    let fired = false;
    const done = () => {
      if (fired) return;
      fired = true;
      clearTimeout(fallback);
      this._panel.removeEventListener('transitionend', done);
      this.onLaunch?.(config);
    };
    const fallback = setTimeout(done, 500);
    this._panel.addEventListener('transitionend', done);
    requestAnimationFrame(() => this.el.classList.add('is-collapsing'));
  }

  /**
   * The config "Fly Now" would launch with — or, given `overrides`, the same
   * thing for another mission/condition without touching the panel (the
   * first-run intro launches FT-101 this way, behind the start screen's back).
   */
  launchConfig(overrides = {}) {
    const sel = { ...this.selection, ...overrides };
    return {
      scenarioIndex: sel.scenarioIndex,
      combatSpawn: sel.combatSpawn,
      lighting: sel.lighting,
      wind: sel.wind,
      cloudCoverage: sel.cloudCoverage,
      noHud: sel.noHud,
      armament: this._effectiveArmament(sel),
    };
  }

  // ── Programmatic state (kept in sync with the pause menu) ────────────────────
  /** Select a mission by SCENARIOS index, switching to its category first. */
  selectScenario(index) {
    const sc = SCENARIOS[index];
    if (!sc) return;
    this._selectCategory(sc.category || 'Free Flight');
    this.selection.scenarioIndex = index;
    this._refreshMission?.();
    this._renderDetail();
  }

  /** Update the active lighting without re-emitting onLightingChange. */
  setLighting(key) {
    const changed = key !== this.selection.lighting;
    this.selection.lighting = key;
    this._refreshWeather?.();
    // Each condition ships its own tuned deck, so a weather change re-seeds the
    // coverage slider (App drops its override to match — see applyLighting).
    if (changed) this.selection.cloudCoverage = conditionCoverage(key);
    this._syncClouds();
  }

  /** Update the NO HUD checkbox without re-emitting onNoHudChange. */
  setNoHud(noHud) {
    this.selection.noHud = noHud;
    if (this._noHudBox) this._noHudBox.checked = noHud;
  }

  /** Restore the panel to its visible, expanded state (after Exit / before show). */
  reset() {
    this._launching = false;
    this.el.classList.remove('is-collapsing');
  }

  /** Loadout the current selection would launch with (see {@link _effectiveArmament}). */
  get armament() { return this._effectiveArmament(); }

  setVisible(v) { this.el.classList.toggle('is-hidden', !v); }

  // ── Small building blocks ───────────────────────────────────────────────────
  _section(parent, title) {
    const section = el('div', 'panel-section');
    const head = el('div', 'section-title');
    head.appendChild(el('span', null, title));
    section.appendChild(head);
    parent.appendChild(section);
    return section;
  }

  /**
   * Custom slider — a native range input stripped of its chrome and redrawn by
   * `.slider-field` (tick rule, amber fill, bar thumb). The wrapper carries the
   * normalized position in the `--fill` custom property, which is what the CSS
   * paints the filled portion from. Returns a refresh fn that re-reads `get()`.
   */
  _slider(parent, { min, max, step, get, set }) {
    const field = el('div', 'slider-field');
    const input = el('input', 'slider-input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);

    const paint = (v) => field.style.setProperty('--fill', `${((v - min) / (max - min)) * 100}%`);
    const refresh = () => { input.value = String(get()); paint(get()); };
    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      paint(v);
      set(v);
    });

    field.appendChild(input);
    parent.appendChild(field);
    refresh();
    return refresh;
  }

  /** Segmented button group. Returns a refresh fn that re-applies the active class. */
  _segGroup(container, items, getActive, onPick) {
    return this._buttonGroup(container, items, getActive, onPick, 'seg-btn');
  }

  /** Vertical list of selectable buttons. Returns a refresh fn. */
  _listGroup(container, items, getActive, onPick) {
    return this._buttonGroup(container, items, getActive, onPick, 'list-btn');
  }

  /**
   * Mission list — like {@link _listGroup}, but each row carries a mission label
   * plus a right-aligned grade badge (best grade earned, from MissionRecords).
   * Records `this._missionButtons` so {@link refreshMissionGrades} can re-badge
   * the list after a run or a data wipe. Returns an active-state refresh fn.
   */
  _missionListGroup(container, items, getActive, onPick) {
    this._missionButtons = items.map((it) => {
      const b = el('button', 'list-btn mission-btn');
      b.type = 'button';
      b._value = it.value;
      b.appendChild(el('span', 'mission-btn-label', it.label));
      // Only graded rows (combat) carry a badge; others have `code == null`.
      let badge = null;
      if (it.code) {
        badge = el('span', 'mission-grade');
        b.appendChild(badge);
        this._applyGradeBadge(badge, it.code);
      }
      b.addEventListener('click', () => onPick(it.value));
      container.appendChild(b);
      return { btn: b, badge, code: it.code };
    });
    const refresh = () =>
      this._missionButtons.forEach(({ btn }) => btn.classList.toggle('is-active', btn._value === getActive()));
    refresh();
    return refresh;
  }

  /** Fill (or clear) a grade badge from the saved best grade for a mission code. */
  _applyGradeBadge(badge, code) {
    const grade = code ? getGrade(code) : null;
    badge.textContent = grade || '';
    badge.className = grade ? `mission-grade grade-${grade.toLowerCase()}` : 'mission-grade is-empty';
  }

  /** Re-read saved grades and re-badge the current mission list (after a run or a data wipe). */
  refreshMissionGrades() {
    if (!this._missionButtons) return;
    for (const { badge, code } of this._missionButtons) {
      if (badge) this._applyGradeBadge(badge, code);
    }
    // The detail card mirrors the selected mission's saved result, so re-render
    // it too — otherwise a score beaten this run wouldn't show until the player
    // reselected the mission.
    this._renderDetail();
  }

  _buttonGroup(container, items, getActive, onPick, cls) {
    const buttons = items.map((it) => {
      const b = el('button', cls, it.label);
      b.type = 'button';
      b._value = it.value;
      b.addEventListener('click', () => onPick(it.value));
      container.appendChild(b);
      return b;
    });
    const refresh = () => buttons.forEach((b) => b.classList.toggle('is-active', b._value === getActive()));
    refresh();
    return refresh;
  }
}
